import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { Timestamp } from 'firebase/firestore';
import { GameListEntry, HltbInfo } from '../types';
import { hltbSearchUrl, lookupHltb, parseHltbId } from '../hltb';

interface FixHltbModalProps {
  show: boolean;
  onHide: () => void;
  game: GameListEntry | null;
  onSave: (hltb: HltbInfo) => void;
}

const FixHltbModal = ({ show, onHide, game, onSave }: FixHltbModalProps) => {
  const [searchTitle, setSearchTitle] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<HltbInfo | null>(null);
  const [searchMessage, setSearchMessage] = useState('');

  const [link, setLink] = useState('');
  const [hours, setHours] = useState('');

  useEffect(() => {
    if (show && game) {
      setSearchTitle(game.title);
      setResult(null);
      setSearchMessage('');
      setLink(game.hltb?.id ? String(game.hltb.id) : '');
      setHours(game.hltb?.main ? String(game.hltb.main) : '');
    }
  }, [show, game]);

  const handleSearch = async () => {
    if (!game || !searchTitle.trim()) return;
    setSearching(true);
    setResult(null);
    setSearchMessage('');

    try {
      const found = await lookupHltb(game.title, game.year, searchTitle.trim());
      if (found.id) {
        setResult(found);
        setSearchMessage(
          found.main ? `Found: ${found.main}hrs main story` : 'Found, but no main story time'
        );
      } else {
        setSearchMessage('No match. Try a shorter name, or enter it manually below.');
      }
    } catch (err) {
      console.error(err);
      setSearchMessage('HowLongToBeat lookup failed');
    } finally {
      setSearching(false);
    }
  };

  const linkId = parseHltbId(link);
  const hoursValue = Number(hours);
  const linkInvalid = link.trim() !== '' && linkId === null;
  const hoursInvalid = hours.trim() !== '' && !(hoursValue > 0);
  const canSaveManual =
    (linkId !== null || hoursValue > 0) && !linkInvalid && !hoursInvalid;

  const handleSaveManual = () => {
    onSave({
      id: linkId,
      main: hoursValue > 0 ? Math.round(hoursValue * 2) / 2 : null,
      checkedAt: Timestamp.now(),
      manual: true,
    });
  };

  return (
    <Dialog open={show} onClose={onHide} maxWidth="xs" fullWidth>
      <DialogTitle>Fix HLTB for {game?.title}</DialogTitle>

      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Search as
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            fullWidth
            value={searchTitle}
            onChange={(e) => setSearchTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
          />
          <Button
            onClick={handleSearch}
            disabled={searching || !searchTitle.trim()}
            sx={{ minWidth: 80 }}
          >
            {searching ? <CircularProgress size={18} /> : 'Search'}
          </Button>
        </Box>

        {searchMessage && (
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {searchMessage}
            </Typography>
            {result && (
              <Button size="small" onClick={() => onSave(result)}>
                Use this
              </Button>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Enter manually
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Find the game on{' '}
          <Link
            href={hltbSearchUrl(searchTitle || game?.title || '')}
            target="_blank"
            rel="noopener noreferrer"
          >
            HowLongToBeat
          </Link>{' '}
          and paste its link.
        </Typography>

        <TextField
          size="small"
          fullWidth
          label="HLTB link or id"
          placeholder="https://howlongtobeat.com/game/12345"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          error={linkInvalid}
          helperText={linkInvalid ? 'Paste a howlongtobeat.com/game/… link' : ' '}
        />
        <TextField
          size="small"
          fullWidth
          type="number"
          label="Main story hours"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          error={hoursInvalid}
          inputProps={{ min: 0, step: 0.5, inputMode: 'decimal' }}
          sx={{ mt: 1 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onHide}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSaveManual}
          disabled={!canSaveManual}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FixHltbModal;
